import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, QrCode, PauseCircle, Play, Lock, CreditCard, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { StickerReprintButton } from "@/components/StickerReprintButton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const statusClass = (s) => {
  if (s === "active") return "bg-[#00C16E]/10 text-[#008A4F] border-[#00C16E]/30";
  if (s === "suspended") return "bg-[#EF4444]/10 text-[#B91C1C] border-[#EF4444]/30";
  if (s === "expired") return "bg-[#FACC15]/10 text-[#92400E] border-[#FACC15]/30";
  if (s === "pending_payment" || s === "pending_invoice" || s === "pending_activation")
    return "bg-[#FACC15]/10 text-[#92400E] border-[#FACC15]/30";
  return "bg-[#F4F4F5] text-[#71717A] border-[#E4E4E7]";
};

// Friendly label for the status pill — pending_payment,
// pending_invoice and pending_activation render as human-readable
// pending-payment strings instead of raw underscore identifiers.
const statusLabel = (s) =>
  ({
    pending_payment: "Pending payment",
    pending_invoice: "Pending invoice",
    pending_activation: "Pending payment",
  }[s] || s);

export default function Vehicles() {
  const { activeProperty, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState({});
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [bulkPaying, setBulkPaying] = useState(false);

  // Managers on invoice-mode properties cannot manually suspend/reactivate
  // vehicles (backend 403s). Hide the buttons so they don't see a broken
  // affordance and can't game monthly billing by suspending vehicles
  // right before the cron runs.
  const canManuallyToggle =
    user?.role === "super_admin" ||
    activeProperty?.is_demo ||
    (activeProperty?.billing_mode && activeProperty.billing_mode !== "invoice");

  const load = async () => {
    if (!activeProperty) return;
    setLoading(true);
    try {
      const [v, t, b] = await Promise.all([
        api.get(`/vehicles?property_id=${activeProperty.id}`),
        api.get(`/tenants?property_id=${activeProperty.id}`),
        api.get(`/billing/details?property_id=${activeProperty.id}`).catch(() => ({ data: null })),
      ]);
      setRows(v.data || []);
      const map = {};
      (t.data || []).forEach((x) => (map[x.id] = x));
      setTenants(map);
      setBilling(b.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [activeProperty]);

  // Poll Stripe checkout status after redirect back from a vehicle
  // activation checkout. Confirms payment settled → refreshes the
  // vehicle rows so the just-paid row flips from "Pending payment"
  // → "Active".
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await api.get(`/billing/checkout-status/${sid}`);
        const st = res.data?.payment_status;
        if (st === "paid") {
          toast.success("Vehicle activated · QR is now live");
          window.history.replaceState({}, "", window.location.pathname);
          load();
          return;
        }
        if (st === "processing") {
          toast.info("Payment processing — QR will activate once settled");
          window.history.replaceState({}, "", window.location.pathname);
          load();
          return;
        }
        if (attempts < 10) {
          setTimeout(poll, 1500);
        } else {
          window.history.replaceState({}, "", window.location.pathname);
        }
      } catch (_e) {
        // silent — user can refresh; webhook is the source of truth
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/vehicles/${id}/${action}`, { vehicle_id: id, agreed: true });
      toast.success(`Vehicle ${action}d`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Vehicle deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Kick off a one-time Stripe Checkout to activate a single vehicle
  // that was added to a paid property after the free-month window
  // closed. On success Stripe redirects back to /dashboard/vehicles
  // with ?session_id=… — the checkout-status polling in the Billing
  // flow already flips the vehicle to active on that page load.
  const payToActivate = async (id) => {
    setPayingId(id);
    try {
      const res = await api.post(`/vehicles/${id}/activation-checkout`, {
        origin_url: window.location.origin,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Could not start checkout");
        setPayingId(null);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setPayingId(null);
    }
  };

  const payAllPending = async () => {
    if (!activeProperty) return;
    const count = rows.filter((v) => v.status === "pending_activation").length;
    if (!count) return;
    if (
      !window.confirm(
        `Activate all ${count} pending vehicle${count > 1 ? "s" : ""} in one Stripe checkout?`
      )
    )
      return;
    setBulkPaying(true);
    try {
      const res = await api.post(
        `/properties/${activeProperty.id}/pay-pending-activations`,
        { origin_url: window.location.origin }
      );
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Could not start checkout");
        setBulkPaying(false);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setBulkPaying(false);
    }
  };

  const isDemo = activeProperty?.is_demo;
  const isSubscribed = billing?.subscription?.active;
  const canActivate = isDemo || isSubscribed;
  // Phase 50c (Feb 2026): per-vehicle activation payment has been
  // REMOVED. Billing is at the property level — vehicle count feeds
  // the monthly invoice. Any pending_activation rows still visible
  // are legacy data; the startup reconciliation sweeps them to
  // active, but we also keep this count at 0 defensively so the
  // bulk-pay banner never renders again.
  const pendingCount = 0;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="mono-tiny text-[#71717A]">Vehicles</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
            Registered ({rows.length})
          </h1>
          <p className="text-[#71717A] text-sm mt-1">
            Edit or suspend existing vehicles. To add a new tenant + vehicle, go to{" "}
            <Link to="/dashboard/tenants" className="text-[#0F8F4F] font-medium">Tenants</Link>.
          </p>
        </div>
        {activeProperty && (
          <StickerReprintButton
            propertyId={activeProperty.id}
            variant="outline"
            size="sm"
            label="Reprint stickers · $50"
            testId="vehicles-reprint-btn"
          />
        )}
      </div>

      {pendingCount > 0 && (
        <div
          className="bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-md p-4 flex items-center justify-between gap-3 flex-wrap"
          data-testid="vehicles-bulk-pay-banner"
        >
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-[#92400E]" strokeWidth={1.5} />
            <div className="text-sm text-[#92400E]">
              <b>
                {pendingCount} vehicle{pendingCount > 1 ? "s" : ""} pending activation payment.
              </b>{" "}
              Pay them all in one checkout, or pay individually below.
            </div>
          </div>
          <Button
            onClick={payAllPending}
            disabled={bulkPaying}
            className="bg-[#0F8F4F] hover:bg-[#0A6B3B] h-10"
            data-testid="vehicles-bulk-pay-btn"
          >
            {bulkPaying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
                Opening…
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Pay All ({pendingCount})
              </>
            )}
          </Button>
        </div>
      )}

      {!canActivate && (
        <div
          className="bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-md p-4 flex items-center justify-between gap-3 flex-wrap"
          data-testid="vehicles-subscribe-banner"
        >
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-[#92400E]" strokeWidth={1.5} />
            <div className="text-sm text-[#92400E]">
              <b>Account setup payment pending.</b> Add tenants and vehicles
              now — QR verification will activate automatically once your
              account setup payment is complete.
            </div>
          </div>
          <Link to="/dashboard/billing">
            <Button className="bg-[#0F8F4F] hover:bg-[#0A6B3B] h-10" data-testid="vehicles-subscribe-cta">
              Pay setup fee
            </Button>
          </Link>
        </div>
      )}

      <div className="bg-white border border-[#E4E4E7] rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Vehicle</TableHead>
              <TableHead>Plate</TableHead>
              <TableHead>Tenant</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-[#71717A] py-8">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-[#71717A] py-8">
                  No vehicles yet.
                </TableCell>
              </TableRow>
            )}
            {rows.map((v) => (
              <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`}>
                <TableCell>
                  <div className="font-medium">
                    {v.year} {v.make} {v.model}
                  </div>
                  <div className="mono-tiny text-[#71717A]">{v.color}</div>
                </TableCell>
                <TableCell className="font-mono text-sm">
                  {v.license_plate} · {v.state}
                </TableCell>
                <TableCell>
                  <div className="text-sm">{tenants[v.tenant_id]?.full_name || "—"}</div>
                  <div className="mono-tiny text-[#71717A]">
                    Unit {tenants[v.tenant_id]?.unit_number || "—"}
                  </div>
                </TableCell>
                <TableCell>
                  <span
                    className={`inline-block px-2.5 py-0.5 border rounded-full text-xs font-semibold uppercase ${statusClass(
                      v.status
                    )}`}
                    data-testid={`vehicle-status-${v.id}`}
                  >
                    {statusLabel(v.status)}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                    <Link to={`/dashboard/qr-codes/${v.id}`}>
                      <Button
                        size="icon"
                        variant="ghost"
                        title="View QR"
                        data-testid={`vehicle-qr-${v.id}`}
                      >
                        <QrCode className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </Link>
                    {canManuallyToggle && v.status === "active" ? (
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Suspend"
                        onClick={() => act(v.id, "suspend")}
                        data-testid={`vehicle-suspend-${v.id}`}
                      >
                        <PauseCircle className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    ) : canManuallyToggle && v.status === "suspended" ? (
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Reactivate"
                        onClick={() => act(v.id, "reactivate")}
                        data-testid={`vehicle-reactivate-${v.id}`}
                      >
                        <Play className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    ) : null}
                    <Link to={`/dashboard/vehicles/new?edit=${v.id}`}>
                      <Button size="icon" variant="ghost" title="Edit">
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </Link>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => remove(v.id)}
                      data-testid={`vehicle-delete-${v.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-[#EF4444]" strokeWidth={1.5} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
